<template>
  <HgListingCarousel
    :teaserData="teaserData"
    :theme="theme"
    :nextButtonTitle="nextButtonTitle"
    :prevButtonTitle="prevButtonTitle"
    :isNewConstruction="shouldShowNewConstructionBadge"
    @listingClicked="(index: number) => emit('listingClicked', index)"
    @listingCarouselMounted="
      (visibleSlides: number) => emit('listingCarouselMounted', visibleSlides)
    "
    @listingCarouselChanged="(activeSlide: number) => emit('listingCarouselChanged', activeSlide)"
  ></HgListingCarousel>
</template>

<script lang="ts" setup>
import { HgListingCarousel, ListingTeaserData } from '@smg-real-estate/domus';
import { Listing } from '@smg-real-estate/hg-rets-schema';
import { HgSupportedLanguage } from '@smg-real-estate/hg-search-libs';

import {
  getListingAddress,
  getListingFormattedPrice,
  getListingImage,
  getListingLivingSpace,
  getListingPriceArea,
  getListingPriceInterval,
  getListingRooms,
  getPostalCodeWithLocality,
  getStreet,
} from '@/helpers/ListingData/ListingData';
import { getCurrentPlatformTheme } from '@/helpers/platformDetection';
import { getPageRoute } from '@/helpers/Router/RouterHelper';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { RouteLocationAsRelativeGeneric } from 'vue-router';

const { t, locale } = useI18n();

defineOptions({
  name: 'ListingCarousel',
});

export interface Props {
  listings: Listing[];
  nextButtonTitle: string;
  prevButtonTitle: string;
  shouldShowNewConstructionBadge?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  shouldShowNewConstructionBadge: false,
});

const emit = defineEmits<{
  listingClicked: [index: number];
  listingCarouselMounted: [visibleSlides: number];
  listingCarouselChanged: [activeSlide: number];
}>();

const theme = getCurrentPlatformTheme();

const teaserData = computed((): ListingTeaserData[] => {
  return props.listings.map(toTeaserData);
});

function toTeaserData(listing: Listing): ListingTeaserData {
  const listingFirstImage = getListingImage(listing, 0) ?? {};
  const listingId = listing.id;

  const pageRoute = getPageRoute(
    'detailPage',
    locale.value as HgSupportedLanguage,
    listing.offerType,
    {
      params: { id: listingId },
    },
  );

  let teaserDataDestination: { to?: RouteLocationAsRelativeGeneric; href?: string } = {
    to: pageRoute,
  };

  if (props.shouldShowNewConstructionBadge) {
    teaserDataDestination = {
      href: String(listingId),
    };
  }

  return {
    ...(teaserDataDestination as ListingTeaserData),
    // TODO: Fix this in ListingTeaserData type because listing might have no images
    imageUrl: listingFirstImage.url || '',
    imageTitle: listingFirstImage.title ?? (t('listingPictureDefaultAltText') as string),
    price:
      getListingFormattedPrice(listing, locale.value) ||
      t('recommendations.priceOnRequest').toString(),
    priceArea: getListingPriceArea(listing),
    priceInterval: getListingPriceInterval(listing),
    livingSpace: getListingLivingSpace(listing),
    numberOfRooms: getListingRooms(listing),
    street: getStreet(listing),
    postalCodeWithLocality: getPostalCodeWithLocality(listing),
    oneLineAddress: getListingAddress(listing),
  };
}
</script>
